import { useState, memo, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { AppBar, Toolbar, Typography, Menu, MenuItem, IconButton, Button, Paper, Breadcrumbs, Box } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ExpandMore, MoreVert as MoreIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { Image } from "mui-image";

import { jwt, titleCase } from "../utils/index.js";
import { LabsByPathMap } from "../utils/constants/index.js";
import { slugToLabel } from "../utils/slug-utils.js";
import { LAB_TABS_MAP } from "../utils/lab-tabs.js";
import logo from "../assets/images/logo.png";
import logoutIcon from "../assets/icons/logout.png";

import Search from "./Search.js";
import { useSearchData } from "../hooks/useSearchData.js";

const useStyles = makeStyles((theme) => ({
	grow: {
		flexGrow: 1,
		flexBasis: "auto",
		background: "white",
		zIndex: 1200,
		height: "70px",
	},
	root: {
		height: "30px",
		padding: theme.spacing(0.5),
		borderRadius: "0px",
		background: theme.palette.greyDark.main,
	},
	icon: {
		marginRight: 0.5,
		width: 20,
		height: 20,
	},
	expanded: {
		background: "transparent",
	},
	innerSmallAvatar: {
		color: theme.palette.common.black,
		fontSize: "inherit",
	},
	anchorOriginBottomRightCircular: {
		".MuiBadge-anchorOriginBottomRightCircular": {
			right: 0,
			bottom: 0,
		},
	},
	avatar: {
		width: "30px",
		height: "30px",
		background: "white",
	},
	iconButton: {
		padding: "3px 6px",
	},
	menuItemButton: {
		width: "100%",
		bgcolor: "grey.light",
		"&:hover": {
			bgcolor: "grey.dark",
		},
	},
	grey: {
		color: "grey.500",
	},
}));

const ButtonWithText = ({ text, icon, more, handler }) => (
	<Button sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1, mx: 1 }} onClick={(event) => handler(event)}>
		<Image src={icon} alt={text} fit="contain" sx={{ p: 0, my: 0, height: "100%", maxWidth: "200px" }} />
		<Typography align="center" color="primary.main" fontSize="small" fontWeight="bold" display="flex" alignItems="center" sx={{ textTransform: "capitalize" }}>
			{text}
			{more && <ExpandMore />}
		</Typography>
	</Button>
);

const Header = ({ isAuthenticated }) => {
	const classes = useStyles();

	const location = useLocation();
	const navigate = useNavigate();
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
	const [searchFilter, setSearchFilter] = useState("");
	const [searchResults, setSearchResults] = useState([]);

	// Search index is built once in the shared useSearchData hook (single source of truth)
	const searchData = useSearchData();

	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

	const handleMobileMenuClose = () => setMobileMoreAnchorEl(null);
	const handleMobileMenuOpen = (event) => setMobileMoreAnchorEl(event.currentTarget);
	const closeAll = () => {
		handleMobileMenuClose();
	};

	const handleSearchChange = (event) => {
		const query = event.target.value.toLowerCase().trim();
		setSearchFilter(query);
	};

	const matchProductFields = (product, query) => {
		if (product.description && product.description.toLowerCase().includes(query)) return true;
		if (product.collections && Array.isArray(product.collections)) {
			const hasMatch = product.collections.some((col) =>
				(typeof col === "string" && col.toLowerCase().includes(query))
				|| (col.text && col.text.toLowerCase().includes(query))
			);
			if (hasMatch) return true;
		}
		if (product.prices && product.prices.products) {
			const hasMatch = product.prices.products.some((p) => p.toLowerCase().includes(query));
			if (hasMatch) return true;
		}
		if (product.production && product.production.products) {
			const hasMatch = product.production.products.some((p) => p.toLowerCase().includes(query));
			if (hasMatch) return true;
		}
		return false;
	};

	const matchLabFields = (lab, query) => {
		if (lab.description && lab.description.toLowerCase().includes(query)) return true;
		if (lab.region && lab.region.toLowerCase().includes(query)) return true;
		return false;
	};

	// Debounced search filtering
	useEffect(() => {
		if (searchFilter.length === 0) {
			setSearchResults([]);
			return;
		}

		const timeoutId = setTimeout(() => {
			const query = searchFilter;

			// Prioritize direct name matches
			const nameMatches = searchData.filter((item) => item.name.toLowerCase().includes(query));

			let filteredResults = nameMatches;

			if (nameMatches.length < 5) {
				// Enhanced filtering logic to search across multiple fields
				const otherResults = searchData.filter((item) => {
					if (nameMatches.includes(item)) return false;

					if (item.type === "map" && item.product?.subheader) {
						return false;
					}

					if (item.product && matchProductFields(item.product, query)) return true;

					if (item.lab && matchLabFields(item.lab, query)) return true;

					return false;
				});

				filteredResults = [...nameMatches, ...otherResults];
			}

			setSearchResults(filteredResults);
		}, 300);

		return () => clearTimeout(timeoutId);
	}, [searchFilter, searchData]);

	const CrumpLink = styled(Link)(({ theme }) => ({ display: "flex", color: theme.palette.primary.main }));

	const buttons = [
		{
			icon: logoutIcon,
			text: "Logout",
			handler: () => {
				closeAll();
				jwt.destroyToken();
				navigate("/");
			},
		},
	];

	const renderMobileMenu = (
		<Menu
			keepMounted
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			{buttons.map((button) => (
				<MenuItem key={button.text} onClick={button.handler}>
					<Image src={button.icon} width="20px" />
					<p style={{ marginLeft: "5px" }}>{button.text}</p>
					{button.more && <ExpandMore />}
				</MenuItem>
			))}
		</Menu>
	);

	const pathnames = location.pathname.split("/").filter(Boolean);
	const crumps = [];
	crumps.push(
		<CrumpLink to="/">
			{"Home"}
		</CrumpLink>,
	);

	for (const [ind, path] of pathnames.entries()) {
		let text = titleCase(path);
		// eslint-disable-next-line no-continue
		if (path === "home") continue;

		// Check if the current full path matches any lab path
		const currentPath = `../${pathnames.slice(0, ind + 1).join("/")}`;
		const matchingLab = LabsByPathMap.get(currentPath);
		if (matchingLab) {
			text = `Living Labs / ${matchingLab.title}`;
		} else {
			// Check if the previous segment was a lab and this segment is a tab slug
			if (ind > 0) {
				const prevPath = `../${pathnames.slice(0, ind).join("/")}`;
				const prevLab = LabsByPathMap.get(prevPath);
				if (prevLab) {
					// Extract the lab base path (e.g., "concatll" from "../concatll")
					const labBasePath = pathnames[ind - 1].toLowerCase();
					const validTabs = LAB_TABS_MAP[labBasePath];
					if (validTabs) {
						// Try to convert the slug back to a human-readable label
						const tabLabel = slugToLabel(path, validTabs);
						if (tabLabel) {
							text = tabLabel;
						}
					}
				}
			}

			// Apply default case-insensitive transformations if not already handled
			if (text === titleCase(path)) {
				switch (path) {
					case "file-upload": {
						text = "File Upload";
						break;
					}

					case "socialimpact": {
						text = "Social Impact";
						break;
					}

					default:
				}
			}
		}

		crumps.push(<CrumpLink to={`/${pathnames.slice(0, ind + 1).join("/")}`}>{text}</CrumpLink>);
	}

	return (
		<>
			<AppBar id="header" position="static" className={classes.grow}>
				<Toolbar className="header-container">
					<Box component={Link} to="/">
						<Image src={logo} alt="Logo" sx={{ p: 0, my: 0, height: "100%", maxWidth: "130px" }} />
					</Box>
					<Box className={classes.grow} style={{ height: "100%" }} />
					{isAuthenticated
						&& (
							<>
								<Box
									sx={{
										display: { xs: "flex", sm: "flex", md: "flex" },
										alignItems: "center",
										ml: 2,
										width: { xs: "108px", sm: "200px", md: "300px" },
									}}
								>
									<Search value={searchFilter} results={searchResults} onChange={handleSearchChange} />
								</Box>
								<Box sx={{ display: { xs: "none", sm: "none", md: "flex" }, height: "100%", py: 1 }}>
									{buttons.map((button) => (
										<ButtonWithText
											key={button.text}
											icon={button.icon}
											text={button.text}
											handler={button.handler}
											more={button.more}
										/>
									))}
								</Box>
								<Box sx={{ display: { xs: "flex", sm: "flex", md: "none" } }}>
									<IconButton onClick={handleMobileMenuOpen}>
										<MoreIcon sx={{ color: "primary.main" }} />
									</IconButton>
								</Box>
							</>
						)}
				</Toolbar>
			</AppBar>
			{isAuthenticated
				&& (
					<Paper elevation={0} className={classes.root}>
						<Breadcrumbs className="header-container">{crumps.map((e, ind) => <div key={`crump_${ind}`}>{e}</div>)}</Breadcrumbs>
					</Paper>
				)}
			{isAuthenticated
				&& (
					<>
						{renderMobileMenu}
					</>
				)}
		</>
	);
};

export default memo(Header);
